import React, { useEffect, useState, useCallback } from 'react';
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControlLabel, 
    Switch, 
    CircularProgress,
    IconButton,
    Tooltip,
    Paper,
    Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

/**
 * Table Controls Component
 * @param {Object} props - Component props
 * @param {boolean} props.showLowPerformance - Whether low performance filter is active
 * @param {Function} props.onToggleLowPerformance - Callback to toggle low performance filter
 * @param {Object} props.filters - Current filters
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {boolean} props.statusLoading - Whether status is loading
 * @param {Function} props.onRefreshStatuses - Callback to refresh statuses
 * @param {Function} props.onForceUpdate - Callback to force an update
 * @param {number} props.totalReports - Total number of reports
 * @param {number} props.filteredReports - Number of filtered reports
 */
const TableControls = ({ 
    showLowPerformance, 
    onToggleLowPerformance, 
    filters, 
    onFiltersChange, 
    statusLoading, 
    onRefreshStatuses,
    onForceUpdate,
    totalReports = 0,
    filteredReports = 0
}) => {
    // Log when showLowPerformance changes
    useEffect(() => {
        console.debug('TableControls received showLowPerformance:', showLowPerformance);
    }, [showLowPerformance]);

    // Force a re-render
    const [localForceUpdate, setLocalForceUpdate] = useState(0);
    const handleForceUpdate = () => {
        console.debug('Forcing update, current showLowPerformance:', showLowPerformance);
        setLocalForceUpdate(prev => prev + 1);
        if (onForceUpdate) {
            onForceUpdate();
        }
    };

    // Local state for search input with debounce
    const [searchInput, setSearchInput] = useState(filters.search || '');
    const [isTyping, setIsTyping] = useState(false);
    
    // Handle input change without debounce
    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        setIsTyping(true);
    };
    
    // Debounced search effect
    useEffect(() => {
        if (!isTyping) return;
        
        const debounceTimeout = setTimeout(() => {
            console.debug('Search debounce completed, applying filter:', searchInput);
            setIsTyping(false);
            
            // Apply the actual filter
            onFiltersChange({
                ...filters,
                search: searchInput
            });
        }, 300); // 300ms debounce delay
        
        return () => clearTimeout(debounceTimeout);
    }, [searchInput, isTyping, filters, onFiltersChange]);
    
    // Sync local state with props when filters change externally
    useEffect(() => {
        if (!isTyping && filters.search !== searchInput) {
            setSearchInput(filters.search || '');
        }
    }, [filters.search, isTyping, searchInput]);

    return (
        <Box sx={{ mb: 2 }}>
            <Paper
                elevation={3}
                sx={{
                    p: 1,
                    borderRadius: '8px',
                    bgcolor: '#2c3040',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 1,
                    border: 'none'
                }}
            >
                {/* Left side controls */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1,
                    flexGrow: 1
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 0.25
                    }}>
                        <TextField
                            placeholder="Search campaigns..."
                            variant="outlined"
                            size="small"
                            value={searchInput}
                            onChange={handleSearchInputChange}
                            InputProps={{
                                startAdornment: <FilterAltIcon sx={{ color: 'rgba(255,255,255,0.5)', mr: 0.5, fontSize: '0.9rem' }} />,
                                sx: { 
                                    color: 'white', 
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    },
                                    height: '32px'
                                }
                            }}
                            sx={{ 
                                width: { xs: '100%', sm: '220px' },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                    padding: '6px 10px',
                                    fontSize: '0.8rem'
                                }
                            }}
                        />
                        {filters.search && (
                            <Typography variant="caption" sx={{ color: '#90caf9', fontSize: '0.65rem', display: 'flex', alignItems: 'center' }}>
                                <Box component="span" sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: '#90caf9', 
                                    display: 'inline-block',
                                    mr: 0.5
                                }}/>
                                Local filtering active
                            </Typography>
                        )}
                        {isTyping && (
                            <Typography variant="caption" sx={{ color: '#ffb74d', fontSize: '0.65rem', display: 'flex', alignItems: 'center' }}>
                                <Box component="span" sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: '#ffb74d', 
                                    display: 'inline-block',
                                    mr: 0.5
                                }}/>
                                Typing...
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        flexWrap: 'wrap',
                        gap: 0.5
                    }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onRefreshStatuses}
                            disabled={statusLoading}
                            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
                            size="small"
                            sx={{ 
                                py: 0.5, 
                                px: 1, 
                                fontSize: '0.75rem',
                                height: '28px'
                            }}
                        >
                            Refresh Statuses
                        </Button>
                        
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={onForceUpdate}
                            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
                            size="small"
                            sx={{ 
                                py: 0.5, 
                                px: 1, 
                                fontSize: '0.75rem',
                                height: '28px'
                            }}
                        >
                            Refresh Table
                        </Button>
                    </Box>
                </Box>
                
                {/* Right side controls */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    alignItems: 'center',
                    gap: 1
                }}>
                    {/* Simplified low performance filter toggle */}
                    <Paper
                        elevation={1}
                        sx={{ 
                            p: 0.5, 
                            bgcolor: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showLowPerformance}
                                    onChange={onToggleLowPerformance}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FilterAltIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }} />
                                    <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                                        Low Performance Filter
                                    </Typography>
                                </Box>
                            }
                            sx={{ m: 0 }}
                        />
                    </Paper>
                </Box>
            </Paper>
        </Box>
    );
};

export default TableControls; 